<template>
  <v-flex>
    <div v-if="!loading" class="order-form">
      <!-- Encabezado -->
      <v-card flat class="order-header rounded-xl pa-4 pa-md-5 mb-4">
        <div class="d-flex flex-wrap align-center">
          <v-avatar
            :color="is_reception ? 'success' : 'primary'"
            size="52"
            class="mr-4 elevation-2"
          >
            <v-icon dark size="28">{{ is_reception ? "mdi-warehouse" : "receipt_long" }}</v-icon>
          </v-avatar>
          <div class="flex-grow-1 order-header__text">
            <div class="text-overline grey--text text--darken-1 order-header__overline">
              {{ is_reception ? "Recepción de mercancía" : "Orden de compra" }}
            </div>
            <div class="order-header__title">{{ title }}</div>
            <div
              v-if="filters.business != null"
              class="text-body-2 grey--text text--darken-1 text-truncate"
            >
              <v-icon small class="mr-1">corporate_fare</v-icon>{{ filters.business.text }}
              <span v-if="filters.location != null"> · {{ filters.location.text }}</span>
            </div>
          </div>
          <div class="d-flex flex-wrap align-center mt-3 mt-md-0">
            <v-chip v-if="currencyCode" small label outlined color="primary" class="mr-2 font-weight-bold">
              <v-icon left small>mdi-cash</v-icon>{{ currencyCode }}
            </v-chip>
            <v-chip small label outlined color="grey darken-1">
              <v-icon left small>mdi-format-list-bulleted</v-icon>{{ items.length }} líneas
            </v-chip>
          </div>
        </div>

        <!-- Resumen de recepción -->
        <v-row v-if="is_reception" dense class="mt-4">
          <v-col cols="6" md="3">
            <div class="stat-tile">
              <div class="stat-tile__label">Líneas pendientes</div>
              <div class="stat-tile__value">{{ items.length }}</div>
            </div>
          </v-col>
          <v-col cols="6" md="3">
            <div class="stat-tile">
              <div class="stat-tile__label">Unidades pendientes</div>
              <div class="stat-tile__value">{{ receptionSummary.pending }}</div>
            </div>
          </v-col>
          <v-col cols="6" md="3">
            <div class="stat-tile">
              <div class="stat-tile__label">A recibir</div>
              <div class="stat-tile__value success--text">
                {{ receptionSummary.receiving }}
                <span class="stat-tile__hint">en {{ receptionSummary.lines }} líneas</span>
              </div>
            </div>
          </v-col>
          <v-col cols="6" md="3">
            <div class="stat-tile stat-tile--accent">
              <div class="stat-tile__label">Total a recibir</div>
              <div class="stat-tile__value">
                {{ total_received.tot_amount }}
                <span class="stat-tile__hint">{{ currencyCode }}</span>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card>

      <v-form ref="form" v-model="valid" lazy-validation>
        <!-- Ubicación -->
        <v-card v-show="!allDisabled" outlined class="rounded-xl mb-4">
          <div class="section-title">
            <v-icon small class="mr-2">mdi-map-marker-outline</v-icon>Ubicación
          </div>
          <v-row dense class="px-4 pb-1">
            <v-col cols="12" sm="6" md="4" lg="3">
              <v-autocomplete
                outlined
                dense
                v-model="filters.business"
                :items="itemsBussines"
                label="Unidad de negocio"
                :rules="[(v) => !!v || 'Unidad de negocio es requerido']"
                color="cyanText"
                :disabled="allDisabled"
                @change="loadLogo"
                return-object
                prepend-inner-icon="corporate_fare"
                required
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="3">
              <v-autocomplete
                outlined
                dense
                v-model="filters.location"
                :rules="[(v) => !!v || 'Ubicacion es requerido']"
                :items="
                  filters.business != null
                    ? itemsLocations.filter(
                        (element) => element.bus_id == filters.business.value
                      )
                    : []
                "
                return-object
                :disabled="allDisabled"
                @change="updateWarehouse"
                color="cyanText"
                label="Location"
                prepend-inner-icon="corporate_fare"
                required
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="2">
              <v-autocomplete
                v-model="filters.warehouse_id"
                :disabled="allDisabled"
                :items="itemsWarehouse"
                color="cyanText"
                label="Almacen"
                item-value="id"
                item-text="name"
                prepend-inner-icon="business"
                outlined
                required
                dense
              >
              </v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6" md="6" lg="2">
              <v-autocomplete
                outlined
                dense
                v-model="filters.department_id"
                :disabled="allDisabled"
                :items="itemsDepartment"
                :rules="[(v) => !!v || 'Departamento es requerido']"
                return-object
                color="cyanText"
                label="Departamento"
                prepend-inner-icon="corporate_fare"
                required
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="12" md="6" lg="2">
              <v-autocomplete
                v-model="filters.project"
                :items="projectFilter"
                :disabled="allDisabled"
                label="Proyecto"
                color="cyanText"
                return-object
                item-value="id"
                :item-text="projectText"
                prepend-inner-icon="folder_open"
                outlined
                dense
                required
              >
              </v-autocomplete>
            </v-col>
          </v-row>
        </v-card>

        <v-card outlined class="rounded-xl">
          <!-- Datos generales -->
          <div class="section-title">
            <v-icon small class="mr-2">mdi-card-account-details-outline</v-icon>Datos generales
          </div>
          <v-row dense class="px-4">
            <v-col cols="12" md="6">
              <div class="d-flex align-start">
                <v-text-field
                  :rules="[(v) => !!v || 'Suplidor es requerido']"
                  :value="selectedEntity != null ? selectedEntity.name : ''"
                  dense
                  outlined
                  readonly
                  label="Suplidor"
                  prepend-inner-icon="mdi-truck-outline"
                >
                </v-text-field>
                <searchClients
                  v-if="
                    !data_order ||
                    !data_order.length ||
                    data_order[0].last_status_id < 2
                  "
                  @close="dialogSearch = false"
                  :types_clients="1"
                  @selected-item="selectItem($event)"
                />
              </div>
              <v-text-field
                :value="
                  selectedEntity != null ? selectedEntity.document_id : ''
                "
                dense
                outlined
                readonly
                label="RNC o Cedula"
                prepend-inner-icon="mdi-card-account-details-outline"
              >
              </v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-if="is_reception"
                    v-model="invoice_supplier"
                    dense
                    outlined
                    color="success"
                    label="Factura suplidor"
                    prepend-inner-icon="mdi-file-document-outline"
                  >
                  </v-text-field>
                  <v-text-field
                    v-else
                    :value="
                      selectedEntity != null
                        ? selectedEntity.phone_number
                        : ''
                    "
                    dense
                    outlined
                    readonly
                    label="Telefono"
                    prepend-inner-icon="mdi-phone-outline"
                  >
                  </v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-autocomplete
                    v-model="selectedCreditDays"
                    :items="credit_days"
                    item-text="name"
                    item-value="id"
                    label="Terminos de pago"
                    prepend-inner-icon="mdi-credit-card-clock"
                    dense
                    :disabled="is_reception"
                    outlined
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-autocomplete
                    v-model="route_id"
                    :items="routes"
                    item-text="name"
                    item-value="id"
                    label="Ruta"
                    prepend-inner-icon="mdi-source-branch"
                    outlined
                    dense
                  >
                  </v-autocomplete>
                </v-col>
                <v-col cols="12" sm="6" class="d-flex align-center mb-6">
                  <span class="text-caption grey--text text--darken-1 mr-3">Moneda</span>
                  <v-btn-toggle
                    :rules="[(v) => !!v || 'Moneda es requerida']"
                    v-model="seleted_currencies"
                    class="rounded-lg"
                    mandatory
                    dense
                    color="primary"
                  >
                    <v-btn
                      :disabled="is_reception"
                      v-for="(item, i) of currencies"
                      :key="i"
                      :value="item.id"
                      small
                    >
                      {{ item?.code }}
                    </v-btn>
                  </v-btn-toggle>
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <v-divider></v-divider>

          <!-- Artículos -->
          <div class="section-title d-flex flex-wrap align-center">
            <span class="mr-auto">
              <v-icon small class="mr-2">mdi-package-variant-closed</v-icon>
              {{ is_reception ? "Artículos a recibir" : "Artículos" }}
            </span>
            <v-text-field
              v-model="searchProduct"
              class="order-search mt-2 mt-sm-0"
              prepend-inner-icon="mdi-magnify"
              placeholder="Buscar artículo"
              dense
              outlined
              clearable
              hide-details
            ></v-text-field>
          </div>
          <div class="px-2 px-md-4">
            <v-data-table
              class="order-table"
              :headers="headers"
              :items="items"
              dense
              item-key="key"
              :item-class="rowClass"
              :search="searchProduct"
              :mobile-breakpoint="0"
              hide-default-footer
              :items-per-page="items.length"
              no-data-text="No hay artículos"
            >
              <template v-slot:[`item.id`]="{ item }">
                <span class="grey--text text--darken-1">{{ item.id }}</span>
              </template>
              <template v-slot:[`item.item_name`]="{ item }">
                <span class="font-weight-medium">{{ item.name }}</span>
                <v-chip v-if="item.is_offer" x-small color="deep-purple" dark
                  class="ml-2 font-weight-bold">Oferta</v-chip>
              </template>
              <template v-slot:[`item.reference`]="{ item }">
                <v-text-field
                  :readonly="is_reception"
                  hide-details
                  v-model="item.reference"
                  dense
                >
                </v-text-field>
              </template>
              <template v-slot:[`item.warehouse`]="{ item }">
                <v-autocomplete
                  v-model="item.warehouse_id"
                  class="rounded-lg my-1 cell-input cell-input--wide"
                  :items="itemsWarehouse"
                  color="cyanText"
                  :rules="[(v) => !!v || 'Almacen es requerido']"
                  item-value="id"
                  item-text="name"
                  prepend-inner-icon="business"
                  outlined
                  required hide-details
                  dense
                >
                </v-autocomplete>
              </template>
              <template v-slot:[`item.amount`]="{ item }">
                <span v-if="is_reception">{{ currencyFormat(item.amount) }}</span>
                <v-text-field
                  v-else
                  @input="changeAmountAccount(item)"
                  prefix="$" class="rounded-lg my-1 cell-input" outlined
                  v-model="item.amount"
                  dense type="number"
                  hide-details
                >
                </v-text-field>
              </template>
              <template v-slot:[`item.account`]="{ item }">
                <v-autocomplete
                  :readonly="is_reception"
                  v-model="item.item_chart_of_account_id"
                  :items="accounts"
                  item-text="name"
                  item-value="id"
                  label="Cuentas"
                  return-object
                  hide-details
                  single-line
                ></v-autocomplete>
              </template>
              <template v-slot:[`item.discount`]="{ item }">
                <v-text-field
                  v-if="!is_reception"
                  :readonly="item.is_offer"
                  class="cell-input cell-input--narrow"
                  prefix="%"
                  :value="item.discount * 100"
                  @input="val => item.discount = val / 100"
                  dense
                  type="number"
                  hide-details
                />
              </template>

              <template v-slot:[`item.itbis`]="{ item }">
                <span v-if="is_reception">{{ item.itbis?.name }}</span>
                <v-autocomplete
                  v-else
                  outlined
                  dense
                  class="cell-input cell-input--itbis"
                  v-model="item.itbis"
                  :items="itbis"
                  item-text="name"
                  item-value="value"
                  label="itbis"
                  return-object
                  hide-details
                  single-line
                ></v-autocomplete>
              </template>
              <template v-slot:[`item.quantity`]="{ item }">
                <span v-if="is_reception" class="font-weight-medium">{{ item.quantity }}</span>
                <v-text-field
                  v-else
                  v-model="item.quantity"
                  type="number"
                  :min="1"
                  :max="item.requested_quantity"
                  dense class="rounded-lg my-1 cell-input cell-input--narrow" outlined
                  hide-details
                >
                </v-text-field>
              </template>
              <template v-slot:[`item.quantityReceipt`]="{ item }">
                <div class="py-1">
                  <v-text-field
                    class="rounded-lg cell-input cell-input--narrow"
                    outlined
                    color="success"
                    v-model="item.quantityReceipt"
                    type="number"
                    :min="0"
                    :max="item.quantity - item.entry_quantity"
                    :error="parseFloat(item.quantityReceipt) > item.quantity - item.entry_quantity"
                    dense
                    hide-details
                  >
                  </v-text-field>
                  <div class="text-caption grey--text text--darken-1 mt-1 text-no-wrap">
                    Pendiente: <b>{{ item.quantity - item.entry_quantity }}</b>
                    <span v-if="item.entry_quantity > 0"> · Recibido: {{ item.entry_quantity }}</span>
                  </div>
                </div>
              </template>
              <template v-slot:[`item.is_inventoriable`]="{ item }">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-if="item.is_inventoriable" v-bind="attrs" v-on="on" color="success">mdi-open-in-app</v-icon>
                    <v-icon v-else v-bind="attrs" v-on="on" color="error">mdi-inbox-remove-outline</v-icon>
                  </template>
                  <span>Articulo {{ item.is_inventoriable?'':'no' }} inventariable</span>
                </v-tooltip>
              </template>
              <template v-slot:[`item.total`]="{ item }">
                <span class="font-weight-bold text-no-wrap">{{
                  currencyFormat(item.quantity * item.amount + item.itbis.value *  parseFloat(item.amount) *  parseFloat(item.quantity) - (item.discount * item.amount * item.quantity))
                }}</span>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <div>
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        v-if="is_reception == false"
                        @click="viewAccounting(item)"
                        color="success"
                        class="mt-n2"
                        v-bind="attrs"
                        v-on="on"
                      >
                        edit
                      </v-icon>
                    </template>
                    <span>Ajuste contabilidad</span>
                  </v-tooltip>
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        v-if="is_reception == false"
                        @click="deleteProducts(item)"
                        color="red"
                        class="mx-1 mt-n2"
                        v-bind="attrs"
                        v-on="on"
                      >
                        close
                      </v-icon>
                    </template>
                    <span>Eliminar</span>
                  </v-tooltip>
                </div>
              </template>
              <template v-slot:[`item.remove`]="{ item }">
                <v-menu v-if="is_reception == false" offset-y left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon color="grey darken-1" class="mt-n2" v-bind="attrs" v-on="on">
                      mdi-cog
                    </v-icon>
                  </template>
                  <v-list dense>
                    <v-list-item @click="remove_item(item)">
                      <v-list-item-icon class="mr-2">
                        <v-icon small color="error">mdi-delete-outline</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Eliminar línea</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      v-if="!item.is_offer && parseInt(item.quantity) > 1"
                      @click="openSplit(item)"
                    >
                      <v-list-item-icon class="mr-2">
                        <v-icon small color="deep-purple">mdi-call-split</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Dividir línea</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="openAddOferta(item)">
                      <v-list-item-icon class="mr-2">
                        <v-icon small color="teal">mdi-gift-outline</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Agregar oferta</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </template>
            </v-data-table>

            <div v-if="!is_reception" class="d-flex justify-end mt-3">
              <v-btn small @click="showItems" color="primary" outlined class="rounded-lg">
                <v-icon left>add</v-icon>Agregar item
              </v-btn>
            </div>
          </div>

          <!-- Notas y totales -->
          <v-row class="px-4 pt-4 pb-2">
            <v-col cols="12" md="7" lg="8">
              <v-textarea
                v-model="nota"
                outlined
                rows="3"
                auto-grow
                hide-details
                label="Nota"
                prepend-inner-icon="mdi-note-text-outline"
                class="mb-3"
              ></v-textarea>
              <v-textarea
                v-model="reference"
                outlined
                rows="2"
                auto-grow
                hide-details
                label="Referencia"
                prepend-inner-icon="mdi-pound"
              ></v-textarea>
            </v-col>
            <v-col cols="12" md="5" lg="4">
              <div class="totals-card">
                <template v-if="!is_reception">
                  <div class="totals-card__row">
                    <span>Subtotal</span>
                    <span>{{ currencyFormat(parseFloat(totales.tot_amount.replaceAll(',', '')) + parseFloat(totales.total_discount)) }}</span>
                  </div>
                  <div class="totals-card__row">
                    <span>Descuento</span>
                    <span class="error--text">- {{ totales.total_discount }}</span>
                  </div>
                  <v-divider class="my-2"></v-divider>
                  <div class="totals-card__row totals-card__row--total">
                    <span>Total</span>
                    <span>{{ totales.tot_amount }} <small>{{ currencyCode }}</small></span>
                  </div>
                </template>
                <template v-else>
                  <div class="totals-card__row">
                    <span>Unidades a recibir</span>
                    <span>{{ receptionSummary.receiving }}</span>
                  </div>
                  <v-divider class="my-2"></v-divider>
                  <div class="totals-card__row totals-card__row--total">
                    <span>Total recibido</span>
                    <span>{{ total_received.tot_amount }} <small>{{ currencyCode }}</small></span>
                  </div>
                </template>
              </div>
            </v-col>
          </v-row>

          <!-- Acciones -->
          <v-card-actions class="order-actions px-4 py-4">
            <v-spacer class="d-none d-sm-flex"></v-spacer>
            <v-btn
              v-if="is_reception"
              @click="save(3)"
              color="success"
              large
              depressed
              class="rounded-lg px-6"
              :block="$vuetify.breakpoint.xsOnly"
              :loading="saving"
              :disabled="saving"
            >
              <v-icon left>warehouse</v-icon>Guardar recepción
            </v-btn>
            <template v-else>
              <v-btn
                @click="save(1)"
                color="primary"
                outlined
                large
                class="rounded-lg px-6 mb-2 mb-sm-0"
                :block="$vuetify.breakpoint.xsOnly"
              >
                <v-icon left>save</v-icon>Guardar
              </v-btn>
              <v-btn
                @click="save(2)"
                color="success"
                large
                depressed
                class="rounded-lg px-6 ml-0 ml-sm-2"
                :block="$vuetify.breakpoint.xsOnly"
              >
                <v-icon left>send</v-icon>Guardar y enviar
              </v-btn>
            </template>
          </v-card-actions>
        </v-card>
      </v-form>
      <v-dialog width="50%" v-model="dialogWithholdings">
        <v-card class="rounded-xl" width="100%" height="100%">
          <v-toolbar flat dark color="transparent">
            <span
              class="font-weight-semibold"
              style="color: black; font-size: 2vh"
              >Agregar retencion</span
            >
            <v-spacer></v-spacer>
            <v-btn @click="dialogWithholdings = false" icon dark>
              <v-icon color="black">mdi-close</v-icon>
            </v-btn>
            <v-toolbar-title> </v-toolbar-title>
          </v-toolbar>

          <v-card-text>
            <v-row justify="center">
              <v-col cols="6">
                <v-autocomplete
                  outlined
                  dense
                  v-model="selected_withholdings_push"
                  :items="withholdings"
                  item-text="name"
                  item-value="id"
                  label="RETENCIONES"
                  return-object
                  color="cyanText"
                  required
                ></v-autocomplete>
              </v-col>
            </v-row>
            <v-row no-gutters justify="center">
              <v-btn @click="add_withholdings" outlined>Agregar</v-btn>
            </v-row>
          </v-card-text>
        </v-card>
      </v-dialog>
      <v-dialog width="90%" v-model="dialogAccounting">
        <v-card class="rounded-xl" height="90%">
          <v-card-text>
            <v-data-table
              v-if="dialogAccounting"
              :headers="headers_account"
              :items="items[editedIndex].order_chart_of_accounts"
              class="elevation-0 pr-6"
              hide-default-footer
              :items-per-page="10000"
            >
              <template v-slot:top>
                <v-toolbar flat>
                  <v-spacer></v-spacer>

                  <v-btn
                    class="mt-2"
                    @click="addLine()"
                    outlined
                    color="primary"
                  >
                    <v-icon>add</v-icon> Agregar linea
                  </v-btn>
                  <v-icon
                    class="mx-3"
                    size="40"
                    @click="dialogAccounting = false"
                    >close</v-icon
                  >
                </v-toolbar>
              </template>
              <template v-slot:[`item.chart_of_account_id`]="{ item }">
                <v-autocomplete
                  dense
                  item-value="id"
                  item-text="name"
                  v-model="item.chart_of_account_id"
                  return-object
                  :items="accounts"
                  @change="
                    item.dgii_category_id =
                      item.chart_of_account_id.dgii_category_id;
                    resetInvalidAccountCategory(item, categories, item.chart_of_account_id, 'diary_book_category_id', 'diary_book_sub_category_id')
                  "
                  color="cyanText"
                  label="Cuenta contable"
                  outlined
                  required
                ></v-autocomplete>
              </template>
              <template v-slot:[`item.dgii_category_id`]="{ item }">
                <v-autocomplete
                  dense
                  v-model="item.dgii_category_id"
                  :items="categoryDgii"
                  color="cyanText"
                  label="Categoria Dgii"
                  outlined
                  required
                ></v-autocomplete>
              </template>
              <template v-slot:[`item.descripcion`]="{ item }">
                <v-textarea
                  dense
                  rows="2"
                  row-height="10"
                  v-model="item.note"
                  label="Descripción"
                  outlined
                ></v-textarea>
              </template>

              <template v-slot:[`item.department_id`]="{ item }">
                <v-autocomplete
                  clearable
                  dense
                  outlined
                  v-model="item.department_id"
                  :items="itemsDepartment"
                  color="cyanText"
                  label="Departamento"
                  prepend-inner-icon="business"
                  required
                ></v-autocomplete>
              </template>
              <template v-slot:[`item.diary_book_category_id`]="{ item }">
                <v-autocomplete
                  clearable
                  dense
                  outlined
                  v-model="item.diary_book_category_id"
                  :items="categoriesForAccount(categories, item.chart_of_account_id)"
                  @change="item.diary_book_sub_category_id = null"
                  color="cyanText"
                  label="Categoría"
                  item-text="name"
                  item-value="id"
                  prepend-inner-icon="business"
                  required
                ></v-autocomplete>
              </template>
              <template v-slot:[`item.diary_book_sub_category_id`]="{ item }">
                <v-autocomplete
                  clearable
                  dense
                  outlined
                  v-model="item.diary_book_sub_category_id"
                  :items="
                    subcategoriesForAccount(categories, item.chart_of_account_id, item.diary_book_category_id)
                  "
                  color="cyanText"
                  label="Sub-Categoría"
                  item-text="name"
                  item-value="id"
                  prepend-inner-icon="business"
                  required
                ></v-autocomplete>
              </template>
              <template v-slot:[`item.amount`]="{ item }">
                <v-text-field
                  dense
                  v-model="item.amount"
                  label="Monto"
                  type="number"
                  min="0"
                  outlined
                ></v-text-field>
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <v-icon @click="deleteItem(item)" class="mt-n5" color="black"
                  >close</v-icon
                >
              </template>
              <template v-slot:footer>
                <v-row class="mt-n6" justify="center">
                  <v-col cols="12" md="6">
                    <v-row justify="center"> </v-row>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-row justify="end">
                      <v-card
                        style="
                          margin-right: 150px;
                          margin-top: 30px;
                          margin-bottom: 30px;
                        "
                        elevation="0"
                        width="350px"
                        height="100px"
                        outlined
                      >
                        <v-layout fill-height align-center>
                          <v-col cols="12" md="6" align="center">
                            <v-row justify="center">
                              <span> TOTAL </span>
                            </v-row>
                            <v-row justify="center">
                              <!-- <span v-if="items[editedIndex].accounts_edited.length >
                                    0
                                    ">
                                    $
                                    {{
                                      currencyFormat(
                                        totCont.totalAmount *
                                        currency_rate_selected
                                      )
                                    }}
                                  </span> -->
                            </v-row>
                          </v-col>
                          <v-col cols="12" md="6" align="center"> </v-col>
                        </v-layout>
                      </v-card>
                    </v-row>
                  </v-col>
                </v-row>
              </template>
            </v-data-table>
            <div class="text-center">
              <v-btn
                class="mt-2"
                @click="dialogAccounting = false"
                outlined
                color="primary"
              >
                <v-icon>save</v-icon> Guardar
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-dialog
        v-if="filters.location != null"
        width="80%"
        v-model="dialogItems"
      >
        <articles_catalogue
          v-if="dialogItems"
          :type="1"
          :currencie_rate="'1'"
          :selected_currencies="seleted_currencies"
          :itbis="itbis"
          :removeItemSelected="false"
          @selected-item="addItem($event)"
          :data="{
            location: filters.location.id,
            business: filters.business.value,
            warehouse: filters.warehouse_id,
            catalogue: filters.location.catalogue_id,
          }"
          @close-item="dialogItems = false"
        />
      </v-dialog>

      <!-- Diálogo: Dividir línea / Agregar oferta (líneas con 100% de descuento) -->
      <v-dialog v-model="splitDialog" max-width="440">
        <v-card>
          <v-card-title class="text-subtitle-1 font-weight-bold">
            <v-icon left :color="splitMode === 'divide' ? 'deep-purple' : 'teal'">
              {{ splitMode === 'divide' ? 'mdi-call-split' : 'mdi-gift-outline' }}
            </v-icon>
            {{ splitMode === 'divide' ? 'Dividir línea' : 'Agregar oferta' }}
          </v-card-title>
          <v-card-text>
            <p v-if="splitSource" class="text-body-2 mb-3">
              <strong>{{ splitSource.name }}</strong> — cantidad en la línea:
              {{ splitSource.quantity }}
            </p>
            <v-text-field
              v-model.number="splitQty"
              type="number"
              min="1"
              :max="splitSource ? (splitMode === 'divide' ? parseInt(splitSource.quantity) - 1 : parseInt(splitSource.quantity)) : 1"
              label="Unidades en oferta"
              outlined
              dense
              hide-details
            />
            <p class="text-caption grey--text mt-3 mb-0">
              La oferta se crea con el precio normal del artículo y 100% de descuento
              (costo neto 0, pero el inventario se valoriza).
              {{ splitMode === 'divide'
                ? ' Separa parte de lo ya solicitado; la cantidad pagada se ajusta.'
                : ' Se agrega como unidades extra, exentas del tope de lo solicitado.' }}
            </p>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="splitDialog = false">Cancelar</v-btn>
            <v-btn :color="splitMode === 'divide' ? 'deep-purple' : 'teal'" dark @click="confirmSplit">
              {{ splitMode === 'divide' ? 'Dividir' : 'Agregar' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
    <div v-else>Cargando ....</div>
      <DialogConfirm
          v-if="showDialogConfirm"
          :showDialog="showDialogConfirm"
          :confirm_settings="{
            title: 'Seguro que desea continuar?',            
            sub_title: subtitle_confirm,
            btn_affirmative: 'No, Volver',
            btn_negative: 'Si, Continuar',
            icon: 'mdi-alert-circle',
            size: '45%',
          }"
          @closeDialog="confirm_save">
        </DialogConfirm>
      <DialogConfirm
          v-if="showDialogNoInventoriable"
          :showDialog="showDialogNoInventoriable"
          :confirm_settings="{
            title: 'Hay articulos no inventariables!',            
            sub_title: 'Existen articulos no inventariables en la recepcion. Si continua dichos articulos no entraran a inventario, desea continuar?',
            btn_affirmative: 'No, Volver',
            btn_negative: 'Si, Continuar',
            icon: 'mdi-inbox-remove-outline',
            size: '45%',
          }"
          @closeDialog="confirm_recepcion">
        </DialogConfirm>
  </v-flex>
</template>

<script>
import moment from "moment";
import searchClients from "../Income/SearchClientOrSupplier.vue";
import DialogConfirm from "@/components/DialogConfirm.vue";
import articles_catalogue from "../Inventory/ArticleForNewOrder.vue";
import accountCategoryRules from "../../mixins/accountCategoryRules";
export default {
  mixins: [accountCategoryRules],
  components: { searchClients, articles_catalogue,DialogConfirm },
  data: () => ({
    showDialogConfirm: false,
    showDialogNoInventoriable: false,
    check_no_inventoriable: false,
    // Dividir línea / oferta (2x1)
    splitDialog: false,
    splitSource: null,
    splitQty: 1,
    splitMode: "divide", // "divide" = separa lo solicitado | "add" = agrega oferta extra
    validate_tolerance: false,
    id_to_save: null,
    push_pending: false,
    allDisabled: false,
    loading: false,
    saving: false,
    forward_accounting:true,
    valid: true,
    subtitle_confirm:null,
    headers_account: [
      //{ text: "Entidad", value: "entity", width: "12%" },
      { text: "Cuentaa contable", value: "chart_of_account_id", width: "16%" },
      { text: "Categoría dgii", value: "dgii_category_id", width: "12%" },
      { text: "Descripción", value: "descripcion" },
      { text: "Departamento", value: "department_id", width: "12%" },
      { text: "Categoría", value: "diary_book_category_id", width: "12%" },
      {
        text: "Sub Categoría",
        value: "diary_book_sub_category_id",
        width: "12%",
      },
      { text: "Monto", value: "amount", width: "15%" },
      { text: "Actions", value: "actions", width: "5%" },
    ],
    categories: [],
    date: moment().startOf("month").format("YYYY-MM-DD"),
    date2: moment().startOf("month").format("YYYY-MM-DD"),
    request_id: null,
    dialogAccounting: false,
    tab: null,
    categoryDgii: [],
    invoice_supplier: "",
    refresArticles: true,
    selectedCreditDays: null,
    dialogWithholdings: false,
    withholdings: [],
    selected_withholdings_push: null,
    selectedCaja: null,
    tipComprobante: null,
    searchProduct: "",
    menu: false,
    menu2: false,
    loadData: false,
    loadDataRequest: false,
    dialogPayments: false,
    dialogSearch: false,
    dialogItems: false,
    selectedEntity: null,
    disableInput: false,
    personApplicant: "",
    commentText: "",
    itemRequest: [],
    ncfTypes: [],
    routes: [],
    route_id: null,
    currencies: [],

    itemsBussines: [],
    itemsLocations: [],
    itemsLocations2: [],
    itemsDepartment: [],
    withholdings: [],
    locationImage: null,
    selected_withholdings: [],
    selected_invoice_categories: null,
    invoice_categories: [],
    itemsRoutes: [],
    credit_days: [],
    editedItem: [],
    nota: "",
    reference: null,
    items: [],
    boxes: [],
    itbis: [],
    accounts: [],
    allProjects: [],
    itemsWarehouse: [],
    selectedPayments: [],
    catalogue_id: null,
    editedIndex: -1,
    filters: {
      business: null,
      location: null,
      department_id: null,
      warehouse_id: null,
      project: null,
    },
    seleted_currencies: null,
    listEditItem: [],
    listProducts: [],
    headerlistEditItem: [],
    order_id: null,
    request_id: null,
    quotation_id: null,
    dataLocations: [],
    currencies: [],
    commentHeader: "",
    tolerance: null,
  }),
  props: {
    data_order: {
      type: Array,
      default: [],
    },
    is_reception: {
      type: Boolean,
      default: false,
    },
    original_amount: {
      type: Number,
      default: 0,
    },
  },
  async created() {
    if (this.data_order.length > 0) {
      this.seleted_currencies = this.data_order[0].currency_id;
      this.selectedCreditDays= this.data_order[0].credit_days_id;
    }
    await this.getData();

    if (this.data_order.length == 0) {
      this.allDisabled = false;
    } else {
      this.filters.warehouse_id = this.data_order[0].warehouse_id;

      this.allDisabled = true;

      this.filters.business = {
        value: this.data_order[0].business_unit_id,
        text: this.data_order[0].business_unit_description_long,
      };

      this.filters.location = {
        id: this.data_order[0].location_id,
        value: this.data_order[0].location_id,
        text: this.data_order[0].location_description_long,
        catalogue_id: this.data_order[0].catalogue_id,
      };
      await this.updateWarehouse();
      this.filters.department_id = { value: this.data_order[0].department_id };

      this.selectedEntity = { entity_id: this.data_order[0].entity_id };
      this.selectedEntity.name = this.data_order[0].entity_name;
      this.selectedEntity.document_id = this.data_order[0].rnc;
      this.order_id = this.data_order[0].order_id;
      this.request_id = this.data_order[0].order_details[0].request_id;
      this.quotation_id = this.data_order[0].quotation_id;
      this.filters.project = {
        id: this.data_order[0].project_id,
        name: this.data_order[0].project_name,
      };

      let data = [];

      for (let item of this.data_order[0].order_details) {
        let accounts = [];
        if (item.order_chart_of_accounts?.length > 0) {
          for (var acc of item.order_chart_of_accounts) {
            accounts.push({
              chart_of_account_id: acc.chart_of_account_id,
              dgii_category_id: acc.dgii_category_id,
              entry_type_id: acc.entry_type_id,
              department_id: acc.department_id,
              diary_book_category_id:
                acc.categories != null ? acc.categories[0].category_id : null,
              diary_book_sub_category_id:
                acc.subcategories != null
                  ? acc.subcategories[0].category_id
                  : null,
              amount: acc.amount,
            });
          }
        }
          data.push({
            key: item.item_id + item.request_id + this.get_key(),
            id: item.item_id,
            name: item.item_name,
            sequence:item.sequence,
            reference: "",
            project:item.project_code==null?'N/A':`${item.project_code}-${item.project_name}`,
            tax_value: item.tax_value, 
            request_id: parseInt(item.request_id),
            department_id: item.department_id,
            unit: item.unit_name,
            itbis: this.itbis.filter((element) => element.id == item.tax_id)[0],
            discount: item.discount,
            quantity: item.quantity,
            requested_quantity: item.requested_quantity,
            quantityReceipt: 0,
            entry_quantity: item.entry_quantity,
            total: parseFloat(item.price) * parseFloat(item.quantity),
            amount: parseFloat(item.price),
            order_chart_of_accounts: accounts,
            origin_chart_of_accounts: item.order_chart_of_accounts,
            warehouse_id: item.warehouse_id,
            actions: "",
            is_inventoriable: item.is_inventoriable,
            is_offer: item.is_offer === true,
          });

      }

      this.items = this.is_reception?data.filter((element)=>element.quantity - element.entry_quantity>0):data;
      this.nota = this.getCommentText(this.data_order[0].comments);
      this.reference = this.getCommentText(this.data_order[0]?.order_histories[0]?.metadata?.reference);
    }
    this.get_workflow_routes();
  },

  watch: {
    seleted_currencies: {
      handler: async function (newVal, Oldval) {
        try {
          if (
            !this.currencies ||
            !Array.isArray(this.currencies) ||
            this.currencies.length === 0
          ) {
            console.warn("No currencies available");
            return;
          }

          // Find the selected currency
          const curr = this.currencies.find(
            (element) => element.id === this.seleted_currencies
          );
          this.currency_rate = curr ? curr.value : null;

          // Get the currency ID for filtering
          const currency = this.currencies.find(
            (c) => c.id === this.seleted_currencies
          );
          if (!currency) return;

          const currencyId = currency.id;

          // Update items with the new currency
          this.items.forEach((item) => {
            if (!item) return;

            // Update item amount
            if (item.item_currencies && Array.isArray(item.item_currencies)) {
              const amount = item.item_currencies.find(
                (el) => el.currency_id == currencyId
              );
              if (amount && amount.cost_price !== undefined) {
                item.amount = parseFloat(amount.cost_price) || 0;
              }
            }

            // Update order chart of accounts
            if (
              item.order_chart_of_accounts &&
              Array.isArray(item.order_chart_of_accounts)
            ) {
              item.order_chart_of_accounts.forEach((account) => {
                if (
                  account &&
                  account.item_currencies &&
                  Array.isArray(account.item_currencies)
                ) {
                  const accountAmount = account.item_currencies.find(
                    (el) => el.currency_id == currencyId
                  );
                  if (accountAmount && accountAmount.cost_price !== undefined) {
                    account.amount =
                      (parseFloat(accountAmount.cost_price) || 0) *
                      (parseFloat(item.quantity) || 0);
                  }
                }
              });
            }
          });
        } catch (error) {
          console.error("Error in currency change handler:", error);
        }
      },
      immediate: true,
    },
    'totales.tot_amount'(newVal) {
      if(parseFloat(newVal.replaceAll(',',''))!=this.original_amount){
        this.forward_accounting = false;
      }
    },
    forward_accounting(newVal) {
      if(this.order_id==null || this.is_reception){
        return
      }
      this.$toast.info('La contabilidad de la orden se reestablecera a valores por defecto!');
      },
      seleted_currencies(newVal) {
        if(this.data_order.length>0 &&this.seleted_currencies != this.data_order[0].currency_id){
          this.forward_accounting = false;
        }
    }
  },

  computed: {
    selected() {
      return this.items;
    },
    headers() {
      let data = [
        { text: "Solicitud", value: "request_id", width: "3%", align: "start" },
        { text: "ID", value: "id", width: "3%", align: "start" },
        {
          text: "Producto/servicio",
          align: "start",
          value: "item_name",
          width: "20%",
        },
        {
          text: "Referencia",
          value: "reference",
          width: "12%",
          align: " d-none",
          require: false,
          active: false,
        },
        {
          text: "Proyecto",
          value: "project",
          width: "12%",
          align: " d-none",
          require: false,
          active: false,
        },
        {
          text: "Itbis",
          value: "itbis",
          width: "10%",
          align: "start",
          require: false,
          active: false,
        },
        {
          text: "Unidad",
          value: "unit",
          width: "10%",
          align: "start",
          require: false,
          active: true,
        },
      ];

      // Add Almacen column conditionally
      if (this.is_reception === true) {
        data.push({
          text: "Almacen",
          value: "warehouse",
          width: "15%",
          align: "start",
          require: false,
          active: true,
        });
      }

      // Add remaining columns
      data.push(
        {
          text: "Precio",
          value: "amount",
          width: "12%",
          align: "start",
          require: false,
          active: true,
        },
        {
          text: "Descuento",
          value: "discount",
          align: "end",
          width: "5%",
          require: false,
        },
        {
          text: "Cuenta",
          value: "account",
          width: "10%",
          align: " d-none",
          require: false,
          active: false,
        },
        {
          text: "Cantidad",
          value: "quantity",
          width: "10%",
          align: "start",
        },
        {
          text: "Total",
          value: "total",
          width: "10%",
          align: "center",
          require: false,
          active: true,
        }
      );
      if (this.is_reception === true) {
        let index = data.findIndex((element)=>element.value=='discount');
        data[index].align=' d-none';
        data.push({
          text: "Cantidad Recibida",
          value: "quantityReceipt",
          width: this.is_reception ? "10%" : "0%",
          align: this.is_reception ? "start" : "d-none",
          require: false,
          active: this.is_reception,
        });
        data.push({
          text: "",
          value: "is_inventoriable",
          align: this.is_reception ? "end" : "d-none",
          require: false,
          active: this.is_reception,
        });
      }
      if (this.is_reception === false) {
        data[4].align = "center"; // Hide project column in non-reception mode
        data.push({
          text: "",
          value: "remove",
          align: "end",
          width: "3%",
          require: false,
          active: this.is_reception,
        });
      }
      data.push({
        text: "Actions",
        value: "actions",
        sortable: false,
        width: "9%",
        align: " d-none",
        require: false,
        active: false,
      });

      return data;
    },
    title() {
      if (this.data_order.length == 0) {
        return "Nueva orden";
      } else {
        if (this.is_reception == true) {
          return (
            "Recepcion orden #" +
            this.filLeft(this.data_order[0].order_id.toString(), 12, "0")
          );
        }
        return (
          "Editar orden #" +
          this.filLeft(this.data_order[0].order_id.toString(), 12, "0")
        );
      }
    },
    projectFilter() {
      if (this.filters.location != null) {
        let itemsProjects = this.allProjects.filter(
          (element) =>
            element.location_id == this.filters.location.id &&
            element.finished == false
        );
        itemsProjects.unshift({ name: "Todos", id: 0 });
        return itemsProjects;
      }
    },
    totales() {
        let tot_itbis = 0;
        let total_amount = 0;
        let tot_subtotal = 0;
        let total_discount = 0;

        if (this.items && this.items.length > 0) {
          for (let item of this.items) {
            // Aplica precios según moneda si es necesario
            if (this.data_order.length > 0 && !this.data_order[0].order_id) {
              item.order_chart_of_accounts.forEach((account) => {
                account.amount =
                  parseFloat(
                    account.item_currencies.find(
                      (element) => element.currency_id == this.seleted_currencies
                    ).cost_price
                  ) * parseFloat(item.quantity);
              });
            }

            if (item.amount > 0) {
              let quantity = parseFloat(item.quantity);
              let unit_price = parseFloat(item.amount);
              let discount = item.discount || 0; // entre 0 y 1

              let subtotal = unit_price * quantity;
              let discount_amount = subtotal * discount;
              let discounted_subtotal = subtotal - discount_amount;

              let itbis = item.itbis.value * discounted_subtotal;

              tot_itbis += itbis;
              total_discount += discount_amount;
              total_amount += discounted_subtotal + itbis;
              tot_subtotal += discounted_subtotal;
            }
          }
        }

        return {
          tot_itbis: this.currencyFormat(tot_itbis),
          tot_amount: this.currencyFormat(total_amount),
          tot_sub: this.currencyFormat(tot_subtotal),
          tot_discount: this.currencyFormat(total_discount),
          total_itbis: tot_itbis.toFixed(2),
          total_amount: total_amount.toFixed(2),
          total_sub: tot_subtotal.toFixed(2),
          total_discount: total_discount.toFixed(2),
        };
    },
    currencyCode() {
      return this.currencies.find((item) => item.id == this.seleted_currencies)?.code || "";
    },
    receptionSummary() {
      let pending = 0;
      let receiving = 0;
      let lines = 0;
      for (const item of this.items || []) {
        pending += parseFloat(item.quantity || 0) - parseFloat(item.entry_quantity || 0);
        const quantity = parseFloat(item.quantityReceipt || 0);
        receiving += quantity;
        if (quantity > 0) lines++;
      }
      return { pending, receiving, lines };
    },
    total_received() {
        let tot_itbis = 0;
        let total_amount = 0;
        let tot_subtotal = 0;
        let total_discount = 0;

        if (this.items && this.items.length > 0) {
          for (let item of this.items) {
            if (item.amount > 0) {
              let quantity = parseFloat(item.quantityReceipt||0);
              let unit_price = parseFloat(item.amount);
              let discount = item.discount || 0; // entre 0 y 1

              let subtotal = unit_price * quantity;
              let discount_amount = subtotal * discount;
              let discounted_subtotal = subtotal - discount_amount;

              let itbis = item.itbis.value * discounted_subtotal;

              tot_itbis += itbis;
              total_discount += discount_amount;
              total_amount += discounted_subtotal + itbis;
              tot_subtotal += discounted_subtotal;
            }
          }
        }

        return {
          tot_itbis: this.currencyFormat(tot_itbis),
          tot_amount: this.currencyFormat(total_amount),
          tot_sub: this.currencyFormat(tot_subtotal),
          tot_discount: this.currencyFormat(total_discount),
          total_itbis: tot_itbis.toFixed(2),
          total_amount: total_amount.toFixed(2),
          total_sub: tot_subtotal.toFixed(2),
          total_discount: total_discount.toFixed(2),
        };
    }

    // totales() {
    //   let tot_itbis = 0;
    //   let total_amount = 0;
    //   let tot_subtotal = 0;
    //   if (this.items != []) {
    //     for (let item of this.items) {
    //       if (this.data_order.length > 0) {
    //         if (!this.data_order[0].order_id) {
    //           item.order_chart_of_accounts.forEach((account) => {
    //             account.amount =
    //               parseFloat(
    //                 account.item_currencies.find(
    //                   (element) =>
    //                     element.currency_id == this.seleted_currencies
    //                 ).cost_price
    //               ) * parseFloat(item.quantity);
    //           });
    //         }
    //       }

    //       if (item.amount > 0) {
    //         let tot_item = item.amount * item.quantity;
    //         let itbis = item.itbis.value * parseFloat(item.amount) *  parseFloat(item.quantity);
    //         tot_itbis += itbis;
    //         total_amount += tot_item + itbis;
    //         tot_subtotal += tot_item;
    //       }
    //     }
    //   }

    //   return {
    //     tot_itbis: this.currencyFormat(tot_itbis),
    //     tot_amount: this.currencyFormat(total_amount),
    //     tot_sub: this.currencyFormat(tot_subtotal),
    //     total_itbis: tot_itbis.toFixed(2),
    //     total_amount: total_amount.toFixed(2),
    //     total_sub: tot_subtotal.toFixed(2),
    //   };
    // },
  },
  methods: {
    projectText(project) {
      return project.code ? `${project.name} (${project.code})` : project.name;
    },
    rowClass(item) {
      return this.is_reception && parseFloat(item.quantityReceipt) > 0 ? "row-receiving" : "";
    },
    resetSaving() {
      this.saving = false;
    },
    handleInput(e) {},
    changeAmountAccount(item) {
      this.items.forEach((element) => {
        if (element.id == item.id) {
          element.order_chart_of_accounts.forEach((element2) => {
            element2.amount = item.amount;
          });
        }
      });
    },
    async loadLogo() {
      if (this.data_invoice!=undefined && this.data_invoice.length > 0 && this.data_invoice[0].image_storage != null) {
        this.locationImage = await this.get_storage(
          this.data_invoice[0].image_storage
        );
      } else {
        this.locationImage = null;
      }
      this.refresArticles = true;
    },

    async getData() {
      this.loading = true;

      let locations = await this.api_Get("humanResource/locations");
      const data_locations = await locations.json();
      for (let item of data_locations) {
        for (let item2 of this.$store.state.auth.locations) {
          if (item2.id == item.id) {
            this.itemsLocations.push({
              value: item.id,
              text: item.desc_loc_long,
              id: item.id,
              bus_id: item.bus_id,
              desc_bus_short: item.desc_bus_short,
              desc_bus_long: item.desc_bus_long,
              desc_loc_short: item.desc_loc_short,
              desc_loc_long: item.desc_loc_long,
              city_id: item.city_id,
              city_description: item.city_description,
              location_adress: item.location_adress,
              status_id: item.status_id,
              catalogue_id: item.catalogue_id,
              catalogue_name: item.catalogue_name,
              rnl: item.rnl,
              representative_name: item.representative_name,
              sta_description: item.sta_description,
              employee_representative_id: item.employee_representative_id,
            });
          }
        }
      }
      // let resultBoxes = await this.api_Get("income/boxes");
      // this.boxes = await resultBoxes.json();

      // let resultWithholdings = await this.api_Get("accounting/withholdings");
      // this.withholdings = await resultWithholdings.json();
      // let resultCategories = await this.api_Get("income/invoice_categories");
      // this.invoice_categories = await resultCategories.json();

      // let resultTypesNcf = await this.api_Get("accounting/nfc_types");
      // this.ncfTypes = await resultTypesNcf.json();

      let resultCreditDays = await this.api_Get("accounting/credit_days");
      this.credit_days = await resultCreditDays.json();

      let resultItbis = await this.api_Get("accounting/taxes");
      this.itbis = await resultItbis.json();

      const resultCurrencies = await this.api_Get("accounting/currencies");
      this.currencies = await resultCurrencies.json();
      this.itemsBussines = Object.assign(
        [],
        this.$store.state.auth.bussines_units
      );
      let resultTypeAccounts = await this.api_Get("accounting/accountsUsable");
      let accounts = await resultTypeAccounts.json();
      accounts.forEach((element) => {
        element.name = element.name + " (" + element.code + ")";
        // Las cuentas de sistema solo se usan en entradas de diario: se muestran pero no se pueden elegir
        this.accounts.push({
          name: element.code + " - " + element.name + (element.is_system_only ? " (sistema)" : ""),
          id: element.id,
          disabled: !!element.is_system_only,
        });
      });

      this.itemsBussines.unshift({ text: "SELECIONA LA UNIDAD", value: "0" });
      this.filters.business = this.itemsBussines[0].value;
      //this.itemsLocations = this.$store.state.auth.locations;
      const requestProjects = await this.api_Get("project/projects");
      this.allProjects = await requestProjects.json();
      this.itemsLocations2 = this.updateCombo(
        this.itemsLocations,
        this.itemsLocations2,
        this.filters.business,
        "location"
      );
      this.itemsDepartment = await this.g_departments();

      let resultTypeCategories = await this.api_Get(
        "accounting/categories_diary_book"
      );
      this.categories = await resultTypeCategories.json();

      let resultCategoryDgii = await this.api_Get("accounting/category_dgii");
      const dataCategoryDgii = await resultCategoryDgii.json();
      for (let item of dataCategoryDgii) {
        this.categoryDgii.push({ text: item.name, value: item.id });
      }
      //SI ES EDICION DE ORDEN BUSCA LA TOLERANCIA
      if(!this.is_reception && this.data_order.length > 0){ 
        const response = await this.api_Get("shopping/tolerance/"+this.data_order[0].business_unit_id);
        if(response.status == 200){
          const data = await response.json();
            this.tolerance = data.length > 0?parseFloat(data[0].tolerance_percentage):0.1;           
         }
      }
      this.loading = false;
    },
    async loadExchangeMoney() {
      const resultCurrencies = await this.api_Post(
        "accounting/currenciesBusiness",
        {
          business: this.filters.business.value,

          effective_date: new Date(),
        }
      );
      this.currencies = await resultCurrencies.json();
    },
    async get_workflow_routes() {
      if(this.filters.location==null){
        return;
      }
      try {
          const response = await this.api_Post("inventory/show_approval_workflow_routes",
          {
            location_id: this.filters.location.id,
            cant_show_all_routes: false,
          }
        );
         if(response.status != 200){
            this.$toast.error("Error al cargar las rutas de aprobacion");
            return;       
         }
        this.routes = await response.json();
        if(this.data_order.length > 0 && this.data_order[0].workflow_id!=null){
          this.route_id=this.data_order[0].workflow_id;
        }else{
          this.route_id=this.routes.find((element)=>element.is_default==true)?.id||null;
        }
      } catch (error) {
        console.error("Error fetching workflow routes:", error.message);
      }
    },
    getCommentText(comments) {
      try {
        if (!comments) return "";

        // Check if it's already a JSON object
        if (typeof comments === "object") {
          // Handle empty array with empty object case: [{}]
          if (Array.isArray(comments) && comments.length > 0) {
            // Check if it's an empty object
            if (Object.keys(comments[0]).length === 0) {
              return ""; // Empty object, return nothing
            }
            // If it has nota property
            if (comments[0].nota) {
              return comments[0].nota;
            }
          }
          // For other object types, stringify but check if it's meaningful
          const stringified = JSON.stringify(comments);
          return stringified === "[{}]" || stringified === "{}"
            ? ""
            : stringified;
        }

        // If it's a string, try to parse it as JSON
        if (typeof comments === "string") {
          if (comments.trim() === "[{}]" || comments.trim() === "{}") {
            return ""; // Empty JSON string, return nothing
          }

          try {
            const parsedComments = JSON.parse(comments);

            // Handle empty array with empty object case after parsing
            if (Array.isArray(parsedComments) && parsedComments.length > 0) {
              // Check if it's an empty object
              if (Object.keys(parsedComments[0]).length === 0) {
                return ""; // Empty object, return nothing
              }
              // If it has nota property
              if (parsedComments[0].nota) {
                return parsedComments[0].nota;
              }
            }

            // If we've gotten here and it's a simple object
            if (Object.keys(parsedComments).length === 0) {
              return ""; // Empty object
            }

            return comments;
          } catch (e) {
            // If it's not valid JSON, return the string as is
            return comments;
          }
        }

        return "";
      } catch (error) {
        console.error("Error parsing comment:", error);
        return "";
      }
    },
    addItem(item) {
      let itbis = this.itbis.find((element) => element.id == 1);

      for (let it = 0; it < this.items.length; it++) {
        if (item.item_id == this.items[it].id && item.request_id == this.items[it].request_id) {
          this.items[it].quantity++;
        }
      }
      if (
        this.items.find((element) => element.id == item.item_id && element.request_id == item.request_id ) === undefined
      ) {
        let acc = [];

        if (item.item_type_id == 2) {
          acc.push({
            chart_of_account_id:  item.account_categories.expense_chart_of_account_id,
            dgii_category_id: null,
            entry_type_id: 1,
            department_id: null,
            diary_book_category_id: item.account_categories.expense_category_id,
            item_currencies: item.item_currencies,
            diary_book_sub_category_id: item.account_categories.expense_subcategory_id,
            amount: parseFloat(
              item.item_currencies.find(
                (element) => element.currency_id == this.seleted_currencies
              ).cost_price
            ),
          });
        } else if (item.item_type_id == 6) {
          acc.push({
            chart_of_account_id: item.account_categories.expense_subcategory_id,
            dgii_category_id: null,
            entry_type_id: 1,
            department_id: null,
            diary_book_category_id: null,
            item_currencies: item.item_currencies,
            diary_book_sub_category_id: null,
            amount: parseFloat(
              item.item_currencies.find(
                (element) => element.currency_id == this.seleted_currencies
              ).cost_price
            ),
          });
        } else {
          acc.push({
            chart_of_account_id: item.account_categories.inventory_chart_of_account_id,
            dgii_category_id: null,
            entry_type_id: 1,
            department_id: null,
            diary_book_category_id: item.account_categories.inventory_category_id,
            item_currencies: item.item_currencies,
            diary_book_sub_category_id: item.account_categories.inventory_subcategory_id,
            amount: parseFloat(
              item.item_currencies.find(
                (element) => element.currency_id == this.seleted_currencies
              ).cost_price
            ),
          });
        }

        let index = null;
        for (var i = 0; i < this.currencies.length; i++) {
          if (i == this.seleted_currencies) {
            index = this.currencies[i].id;
          }
        }

        let amount = item.item_currencies.filter(
          (element) => element.currency_id == this.seleted_currencies
        )[0].cost_price;
        this.items.push({
          key: item.item_id + item.request_id + this.get_key(),
          id: item.item_id,
          request_id: parseInt(item.request_id),
          requested_quantity:item.requested_quantity,
          name: item.item_name,
          reference: "",
          amount: amount,
          unit: item.unit_name,
          discount: 0,
          itbis: itbis,
          quantity: parseFloat(item.quantity),
          quantityReceipt: 0,
          total: amount * 1,
          item_currencies: item.item_currencies,
          order_chart_of_accounts: acc,
          warehouse_id: item.warehouse_id,
          actions: "",
        });
      }
      this.forward_accounting=false;
     // this.$toast.success("Agregado");
    },
    showItems() {
      if (this.filters.business == null) {
        this.$toast.warning("Debes elegir una unidad de negocio!");
        return;
      }

      if (this.filters.location == null) {
        this.$toast.warning("Debes elegir una location!");
        return;
      }
      if (this.filters.location.catalogue_id == null) {
        this.$toast.warning("Este location no tiene un catalogo de articulo!");
        return;
      }
      if (this.filters.warehouse_id == null) {
        this.$toast.warning("Debe seleccionar un almacen!");
        return;
      }

      this.dialogItems = true;
      // this.items.push({
      //   id: "",
      //   name: "",
      //   reference: "",
      //   amount: "",
      //   discount: "",
      //   itbis: "",
      //   quantity: "",
      //   total: "",
      //   actions: "",
      // });
    },
      get_key() {
        const timestamp = Date.now();
        const numeroAleatorio = Math.floor(Math.random() * 100000);
        return `${timestamp}${numeroAleatorio}`;
      },
    viewAccounting(item) {
      this.editedIndex = this.items.indexOf(item);

      this.dialogAccounting = true;
    },
    updateCombo(itemsOrigen, itemsDestino, condicion, combo) {
      itemsDestino.splice(0, itemsDestino.length);

      for (let item of itemsOrigen) {
        if (item.bus_id === condicion) {
          itemsDestino.push({
            value: item.value,
            text: item.text,
            routes: item.routes,
          });
        }
      }

      //this.filters.location = this.itemsLocations2[0].value;

      return itemsDestino;
    },
    showDialogPayments() {
      if (this.items.length != []) {
        if (this.totales.total_amount > 0) {
          this.dialogPayments = true;
        } else {
          this.$toast.warning("Debes completar los items!");
        }
      } else {
        this.$toast.warning("Debes agregar items para agregar el pago!");
      }
    },
    addLine() {
      this.items[this.editedIndex].order_chart_of_accounts.push({
        chart_of_account_id: null,
        dgii_category_id: null,
        entry_type_id: 1,
        department_id: null,
        diary_book_category_id: null,
        diary_book_sub_category_id: null,
        amount: 0,
      });
    },
    async updateWarehouse() {
      this.items = [];
      this.refresArticles = false;

      const warehouses = await this.api_Get(
        "inventory/warehouseForLocation/" + this.filters.location.value
      );
      this.itemsWarehouse = await warehouses.json();
      this.filters.warehouse_id = this.itemsWarehouse[0].id;
      this.refresArticles = true;
      this.get_workflow_routes();
    },
    add_withholdings() {
      if (this.selected_withholdings_push == null) {
        this.$toast.warning("Debes selecionar una retencion");
        return;
      }
      this.selected_withholdings.push(this.selected_withholdings_push);

      this.dialogWithholdings = false;
    },
    validate() {
      return this.$refs.form.validate();
    },
    confirm_save(response) {
     if(!response){
        this.validate_tolerance=true;
        if(this.id_to_save==2){
          this.push_pending=true;//si el precio sale de la tolerancia y es enviar, inserta status pendiente
        }else{
          this.data_order[0].order_histories.push({
                order_status_id: 1,
                user_id: this.$store.state.auth.user.user.user_id,
                comment: null,
                created_date: null,
              }); // si es solo guardar envia a borrardor
        }
        this.save(this.id_to_save);
      }
      this.showDialogConfirm = false;
    },
    save(id) {
      this.saving = true;
      this.id_to_save = id;
      let items_selected = [];
      let order_details = [];

      if (this.selectedEntity == null) {
        this.$toast.warning("Debes elegir un suplidor!");
        this.saving = false;
        return;
      }
      if (this.filters.business == null) {
        this.$toast.warning("Debes elegir una unidad de negocio !");
        this.saving = false;
        return;
      }
      if (this.filters.location == null) {
        this.$toast.warning("Debes elegir una location !");
        this.saving = false;
        return;
      }
      if (!this.validate()) {
        this.saving = false;
        return;
      }
      if(this.is_reception && this.check_no_inventoriable==false){
        for(let item of this.items){
          if(item.is_inventoriable == false && parseFloat(item.quantityReceipt) > 0){
            this.showDialogNoInventoriable=true;
            this.saving = false;
            return;
          }
        }
      }
      //SI ES EDITANDO EL MONTO DE UNA ORDEN YA APROBADA VERIFICA LA TOLERANCIA
      if(this.is_reception ==false && this.tolerance!=null && this.original_amount > 0 && this.validate_tolerance==false && this.data_order[0].last_status_id > 2 ){
        const amount = parseFloat(this.totales.total_amount);
        function dentroDeTolerancia(montoOriginal, montoNuevo, tolerancia) {
            const limiteInferior = montoOriginal * (1 - tolerancia);
            const limiteSuperior = montoOriginal * (1 + tolerancia);
            return montoNuevo >= limiteInferior && montoNuevo <= limiteSuperior;
          }
         if(dentroDeTolerancia(this.original_amount, amount, parseFloat(this.tolerance))==false){
            this.subtitle_confirm = `El monto total de la orden ${this.currencyFormat(amount)} excede la tolerancia permitida del ${this.tolerance*100}% sobre el monto original de la orden ${this.currencyFormat(this.original_amount)}.
            
            Si desea continuar la oden volvera a estatus pendiente para ser aprobada.`;            
            this.showDialogConfirm = true;
            return;
         }
      }
        //si es enviar y esta en borrador pasa a pendiente
       if((this.id_to_save==2 && this.data_order[0].last_status_id == 1) || this.push_pending){
              this.data_order[0].order_histories.push({
                order_status_id: 2,
                user_id: this.$store.state.auth.user.user.user_id,
                comment: null,
                created_date: null,
              });
        }
      let set_history = this.data_order.length == 0 ? null : this.data_order[0].order_histories;
      let all_items_received = true;
      if (this.items != []) {
        for (let item of this.items) {
          //i++;
          if (item.quantity == 0) {
            this.$toast.warning(
              "la cantidad del item " + item.name + " debe ser mayor que 0"
            );
            this.saving = false;
            return;
          }
          //LAS OFERTAS: precio normal (para valorizar inventario) + 100% de descuento fijo
          if (item.is_offer) {
            item.discount = 1;
            if (!parseFloat(item.amount)) {
              this.$toast.warning(
                "La oferta del item " + item.name + " debe tener su precio normal (no 0)."
              );
              this.saving = false;
              return;
            }
          }
          //SI ES UNA ORDEN VERIFICA QUE LA CANTIDAD A ORDENAR NO EXCEDA LA SOLICITADA
          //Las líneas de oferta quedan exentas (son regalo del suplidor, no cuentan contra lo solicitado)
          if (!this.is_reception && !item.is_offer && item.quantity > item.requested_quantity) {//quantityReceipt
            this.$toast.warning("La cantidad del item " + item.name + " excede la cantidad solicitada!");
            this.saving = false;
            return;
          }
          //SI ES UNA RECEPCION VERIFICA QUE LA CANTIDAD A RECIBIR NO EXCEDA LA ORDENADA
          if (this.is_reception && item.quantityReceipt > (item.quantity - item.entry_quantity)) {
            this.$toast.warning("La cantidad del item " + item.name + " excede la cantidad ordenada!");
            this.saving = false;
            return;
          }
          // if (item.amount == 0) {
          //   this.$toast.warning(
          //     "la precio del item " + item.name + " debe ser mayor que 0"
          //   );
          //   return;
          // }
       
          if(all_items_received && parseFloat(item.quantityReceipt)+item.entry_quantity != item.quantity || 0){
            //si la cantidad que se esta recibiendo mas la cantidad que ya se ha recibido es igual a la ordenada en todos los articulos envia true para cerrar la orden
            all_items_received=false;
          }

          // let accounts = [];
          // if (item.order_chart_of_accounts.length > 0) {
          //   for (let acc of item.order_chart_of_accounts) {
          //     let categories = [];
          //     let subCategories = [];
          //     if (acc.diary_book_category_id) {
          //       categories.push({ category_id: acc.diary_book_category_id });
          //     }
          //     if (acc.diary_book_sub_category_id) {
          //       subCategories.push({
          //         category_id: acc.diary_book_sub_category_id,
          //       });
          //     }

          //     let dep =
          //       this.filters.department_id != null
          //         ? this.filters.department_id.value
          //         : acc.department_id;

          //     let newAmount = null;

          //     accounts.push({
          //       chart_of_account_id:
          //         acc.chart_of_account_id.id == undefined
          //           ? acc.chart_of_account_id
          //           : acc.chart_of_account_id.id,
          //       entry_type_id: acc.entry_type_id,
          //       account_type_id: acc.debito > 0 ? 1 : 2,
          //       location_id: this.filters.location.value,
          //       dgii_category_id: acc.dgii_category_id,
          //       department_id: dep,
          //       categories: categories,
          //       subcategories: subCategories,
          //       item_currencies: item.item_currencies,
          //       amount: parseFloat(acc.amount),
          //     });
          //   }
          // }
          //item.quantity * item.amount + item.itbis.value *  parseFloat(item.amount) *  parseFloat(item.quantity) - (item.discount * item.amount * item.quantity)
          items_selected.push({
            item_id: item.id,
            sequence:item.sequence,
            item_description: item.reference,
            quantity: parseFloat(item.quantity),
            tax_id: item.itbis?.id ?? 1,
            tax_value: item.itbis?.value ?? item.tax_value,
            price: item.amount,
            discount: item.discount,
            request_id: parseInt(item.request_id),
            department_id: item.department_id,
            warehouse_id: item.warehouse_id,
            is_offer: item.is_offer === true,
            // La oferta siempre usa contabilidad por defecto (null) para que el SP
            // genere el inventario + la línea de descuento del 100%.
            order_chart_of_accounts: item.is_offer ? null : (this.forward_accounting ? item.origin_chart_of_accounts : null),
          });

          if (item.quantityReceipt > 0) {
            order_details.push({
              item_id: item.id,
              sequence:item.sequence,
              item_description: item.reference,
              quantity: parseFloat(item.quantityReceipt),
              tax_id: item.tax_id,
              tax_value: item.tax_value,  
              discount: item.discount,
              price: item.amount,
              request_id: parseInt(item.request_id),
              department_id: item.department_id,
              warehouse_id: item.warehouse_id,
              chart_of_account_id: item.item_chart_of_account_id,
            });
          }
        }
      }
      if (this.is_reception == true) {
        if (order_details.length == 0) {
          this.$toast.warning("Debes poner la cantidad minima en 1 articulos");
          this.saving = false;
          return;
        }
      }
      // if( this.data_order.length>0){
      //    this.data_order[0].order_histories.push({
      //      order_status_id: 2,
      //      user_id: this.$store.state.auth.user.user.user_id,
      //      comment: null,
      //      created_date: null,
      //    })
      // }

      let data = {
        type: id,
        order_id: this.order_id,
        request_id: this.request_id,
        quotation_id: this.quotation_id,
        business_unit_id: this.filters.business.value,
        department_id:
          this.filters.department_id != null
            ? this.filters.department_id.value
            : null,
        location_id: this.filters.location.value,
        created_by: this.$store.state.auth.user.user.user_id,
        entity_id: this.selectedEntity.entity_id,
        total_amount: this.totales.total_amount,
        currency_id: this.seleted_currencies,
        note: this.nota,
        reference: this.reference,
        invoice_supplier: this.invoice_supplier,
        order_details: order_details,
        invoice_details: items_selected,
        project: this.filters.project == null ? null : this.filters.project.id,
        warehouse: this.filters.warehouse_id,
        status: set_history,
        all_items_received:all_items_received,
        credit_days_id:this.selectedCreditDays,
        workflow_id:this.route_id,
      };

      this.$emit("save-order", data);
      // El saving se desactiva en el componente padre después de guardar
    },
    selectItem(item) {
      this.selectedEntity = item;
      this.tipComprobante =
      this.selectedEntity.customer_details == null ? null  : this.selectedEntity.customer_details[0].customer.nfc_type_id;
      if(this.selectedEntity.supplier_details!=null && this.selectedEntity.supplier_details.length>0){
        this.selectedCreditDays =  this.selectedEntity.supplier_details == null  ? null  : this.selectedEntity.supplier_details[0].supplier.credit_day_id;
      }

      this.dialogSearch = false;
    },
    selectPayment(item) {
      this.selectedPayments = item;
      this.dialogPayments = false;
    },
    deleteProducts(item) {
      let editIndex = this.items.indexOf(item);
      //this.editedItem = Object.assign({}, item);
      this.items.splice(editIndex, 1);
    },
    delete_withholdings(item) {
      let editIndex = this.selected_withholdings.indexOf(item);
      //this.editedItem = Object.assign({}, item);
      this.selected_withholdings.splice(editIndex, 1);
    },
    deleteItem(item) {
      let editIndex =
        this.items[this.editedIndex].order_chart_of_accounts.indexOf(item);
      //this.editedItem = Object.assign({}, item);

      this.items[this.editedIndex].order_chart_of_accounts.splice(editIndex, 1);
    },
    remove_item(item){
      let editIndex = this.items.indexOf(item);
      if (editIndex > -1) {
        this.items.splice(editIndex, 1);
      }
      this.forward_accounting=false;
    },
    openSplit(item) {
      // Separa lo YA solicitado: parte de la cantidad pasa a oferta (100% desc.)
      this.splitMode = "divide";
      this.splitSource = item;
      this.splitQty = 1;
      this.splitDialog = true;
    },
    openAddOferta(item) {
      // Agrega unidades EXTRA de oferta del mismo item (sube la solicitud al guardar)
      this.splitMode = "add";
      this.splitSource = item;
      this.splitQty = 1;
      this.splitDialog = true;
    },
    confirmSplit() {
      const src = this.splitSource;
      if (!src) return;
      const qty = parseInt(this.splitQty);
      // "divide": máximo = cantidad de la línea - 1 (deja al menos 1 pagada)
      // "add": máximo = cantidad de la línea (típico 2x1/3x2)
      const max =
        this.splitMode === "divide"
          ? parseInt(src.quantity) - 1
          : parseInt(src.quantity);
      if (!qty || qty < 1 || qty > max) {
        this.$toast.warning(`La oferta debe estar entre 1 y ${max} unidad(es).`);
        return;
      }
      // En "divide" se reduce la cantidad pagada; en "add" se deja igual
      if (this.splitMode === "divide") {
        src.quantity = parseInt(src.quantity) - qty;
      }
      const oferta = {
        ...src,
        key: `${src.id}${src.request_id}${this.get_key()}`,
        is_offer: true,
        quantity: qty,
        amount: parseFloat(src.amount) || 0, // precio normal (para valorizar inventario)
        discount: 1, // 100% de descuento => costo neto 0
        total: 0,
        quantityReceipt: 0,
      };
      const idx = this.items.indexOf(src);
      this.items.splice(idx + 1, 0, oferta);
      // La oferta cambia la contabilidad (entra inventario + descuento) aunque el neto sea 0:
      // forzamos el recálculo y el aviso, igual que un cambio de precio.
      this.forward_accounting = false;
      this.splitDialog = false;
      this.splitSource = null;
    },
    close() {
      this.$emit("close-dialog");
    },
    confirm_recepcion(response){
      if(!response){
        this.check_no_inventoriable=true;
        this.save(3);
      }
      this.showDialogNoInventoriable = false;
    }
  },
};
</script>

<style scoped>
.mobile {
  color: #333;
}

@media screen and (max-width: 768px) {
  .mobile table.v-table tr {
    max-width: 100%;
    position: relative;
    display: block;
  }

  .mobile table.v-table tr:nth-child(odd) {
    border-left: 6px solid deeppink;
  }

  .mobile table.v-table tr:nth-child(even) {
    border-left: 6px solid cyan;
  }

  .mobile table.v-table tr td {
    display: flex;
    width: 100%;
    border-bottom: 1px solid #f5f5f5;
    height: auto;
    padding: 10px;
  }

  .mobile table.v-table tr td ul li:before {
    content: attr(data-label);
    padding-right: 0.5em;
    text-align: left;
    display: block;
    color: #999;
  }

  .v-datatable__actions__select {
    width: 50%;
    margin: 0px;
    justify-content: flex-start;
  }

  .mobile .theme--light.v-table tbody tr:hover:not(.v-datatable__expand-row) {
    background: transparent;
  }
}

.flex-content {
  padding: 0;
  margin: 0;
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  width: 100%;
}

.flex-item {
  padding: 5px;
  width: 50%;
  height: 40px;
  font-weight: bold;
}

.folded-paper:before {
  content: "";
  box-shadow: 0 3px 10px rgb(0 0 0 / 0.2);
  position: absolute;
  top: 0;
  right: 0;
  border-width: 0 30px 30px 0;
  border-style: solid;
  border-color: rgb(74, 92, 67) #fff;
  transition: 0.3s;
  border-radius: 0 0 0 4px;
}

.box {
  box-shadow: 0 2.8px 2.2px rgba(0, 0, 0, 0.034),
    0 6.7px 5.3px rgba(0, 0, 0, 0.048), 0 12.5px 10px rgba(0, 0, 0, 0.06),
    0 22.3px 17.9px rgba(0, 0, 0, 0.072), 0 41.8px 33.4px rgba(0, 0, 0, 0.086),
    0 100px 80px rgba(0, 0, 0, 0.12);
}

.order-header {
  background: linear-gradient(135deg, #ffffff 0%, #f1f5fb 100%);
  border: 1px solid #e3e8ef;
}

.order-header__text {
  min-width: 0;
}

.order-header__overline {
  line-height: 1.2;
  letter-spacing: 0.12em !important;
}

.order-header__title {
  font-size: 1.6rem;
  font-weight: 600;
  color: #37474f;
  line-height: 1.25;
  word-break: break-word;
}

.stat-tile {
  height: 100%;
  padding: 10px 14px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #e3e8ef;
}

.stat-tile--accent {
  border-color: #a5d6a7;
  background: #f1f8f1;
}

.stat-tile__label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #78909c;
}

.stat-tile__value {
  font-size: 1.3rem;
  font-weight: 700;
  color: #37474f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.stat-tile__hint {
  font-size: 0.75rem;
  font-weight: 500;
  color: #90a4ae;
}

.section-title {
  padding: 14px 16px 10px;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #546e7a;
}

.order-search {
  max-width: 280px;
  width: 100%;
}

.order-table ::v-deep th {
  white-space: nowrap;
  background: #f7f9fc !important;
  color: #546e7a !important;
  font-weight: 700;
}

.order-table ::v-deep td {
  font-size: 0.85rem !important;
  vertical-align: middle;
}

.order-table ::v-deep tr.row-receiving td {
  background: #f1f8f1;
}

.order-table ::v-deep tr.row-receiving td:first-child {
  box-shadow: inset 3px 0 0 #43a047;
}

.cell-input {
  min-width: 110px;
}

.cell-input--narrow {
  min-width: 90px;
  max-width: 130px;
}

.cell-input--itbis {
  min-width: 170px;
}

.cell-input--wide {
  min-width: 180px;
}

.totals-card {
  padding: 16px 18px;
  border-radius: 12px;
  background: #f7f9fc;
  border: 1px solid #e3e8ef;
}

.totals-card__row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 4px 0;
  font-size: 0.9rem;
  color: #546e7a;
}

.totals-card__row--total {
  font-size: 1.25rem;
  font-weight: 700;
  color: #263238;
}

.order-actions {
  flex-wrap: wrap;
  border-top: 1px solid #e3e8ef;
}

@media (max-width: 600px) {
  .order-header__title {
    font-size: 1.25rem;
  }

  .order-search {
    max-width: none;
  }
}
</style>
